import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import { WalletProvider } from '../contexts/WalletContext';
import BottomNav from '../components/mobile/BottomNav';
import HomeScreen from '../components/mobile/HomeScreen';
import SendMoney from '../components/mobile/SendMoney';
import TopUp from '../components/mobile/TopUp';
import QRScan from '../components/mobile/QRScan';
import Activity from '../components/mobile/Activity';
import MyQR from '../components/mobile/MyQR';
import Receive from '../components/mobile/Receive';
import WalletScreen from '../components/mobile/WalletScreen';
import ProfileScreen from '../components/mobile/ProfileScreen';

const SCREENS = {
  HOME: 'home',
  SEND: 'send',
  TOPUP: 'topup',
  QRSCAN: 'qrscan',
  ACTIVITY: 'activity',
  MYQR: 'myqr',
  RECEIVE: 'receive',
  WALLET: 'wallet',
  PROFILE: 'profile',
};

// Improved Toast — positioned at top inside mobile container, transparent with clear text, auto-dismiss in 3-4 seconds
const Toast = ({ message }) => (
  <motion.div
    data-testid="toast-notification"
    initial={{ opacity: 0, y: -20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -20, scale: 0.95 }}
    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
    style={{
      position: 'absolute',
      top: '16px',
      left: '16px',
      right: '16px',
      zIndex: 9999,
      background: 'rgba(18,18,18,0.85)',
      border: '1px solid rgba(212,175,55,0.25)',
      borderRadius: '14px',
      padding: '14px 18px',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    <span
      style={{
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: 'rgba(74,222,128,0.15)',
        border: '1px solid rgba(74,222,128,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        color: '#4ADE80',
        flexShrink: 0,
      }}
    >
      ✓
    </span>
    <span
      style={{
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: 1.4,
        flex: 1,
      }}
    >
      {message}
    </span>
  </motion.div>
);

// Language toggle button
const LangToggle = ({ lang, setLang }) => (
  <motion.button
    data-testid="lang-toggle"
    whileTap={{ scale: 0.95 }}
    onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '100px',
      padding: '5px 12px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }}
  >
    <span style={{ fontSize: '13px' }}>{lang === 'en' ? '🇲🇦' : '🇬🇧'}</span>
    <span style={{ color: '#8B93A7', fontSize: '11px', fontWeight: '600' }}>
      {lang === 'en' ? 'عر' : 'EN'}
    </span>
  </motion.button>
);

const phoneVariants = {
  initial: { opacity: 0, x: 16 },
  in: { opacity: 1, x: 0 },
  out: { opacity: 0, x: -16 },
};

const DashboardInner = () => {
  const [screen, setScreen] = useState(SCREENS.HOME);
  const [toast, setToast] = useState(null);
  const { lang, t, setLang, isRTL } = useLanguage();

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const navigate = (s) => setScreen(s);
  const goHome = () => setScreen(SCREENS.HOME);

  const sharedProps = {
    onBack: goHome,
    onNavigate: navigate,
    onSuccess: (msg) => { showToast(msg); setTimeout(goHome, 400); },
    onToast: showToast,
    t,
    isRTL,
  };

  const renderScreen = () => {
    switch (screen) {
      case SCREENS.HOME:     return <HomeScreen onNavigate={navigate} t={t} isRTL={isRTL} />;
      case SCREENS.SEND:     return <SendMoney {...sharedProps} />;
      case SCREENS.TOPUP:    return <TopUp {...sharedProps} />;
      case SCREENS.QRSCAN:   return <QRScan {...sharedProps} />;
      case SCREENS.ACTIVITY: return <Activity onBack={goHome} t={t} isRTL={isRTL} />;
      case SCREENS.MYQR:     return <MyQR onBack={goHome} onToast={showToast} t={t} isRTL={isRTL} />;
      case SCREENS.RECEIVE:  return <Receive onBack={goHome} onNavigate={navigate} t={t} isRTL={isRTL} />;
      case SCREENS.WALLET: {
        return <WalletScreen onNavigate={navigate} onToast={showToast} t={t} isRTL={isRTL} />;
      }
      case SCREENS.PROFILE:  return <ProfileScreen onBack={goHome} onNavigate={navigate} t={t} isRTL={isRTL} />;
      default:               return <HomeScreen onNavigate={navigate} t={t} isRTL={isRTL} />;
    }
  };

  const getNavTab = (s) => {
    if (s === SCREENS.HOME) return 'home';
    if ([SCREENS.WALLET, SCREENS.MYQR].includes(s)) return 'wallet';
    if (s === SCREENS.QRSCAN) return 'qrscan';
    if (s === SCREENS.ACTIVITY) return 'activity';
    if (s === SCREENS.PROFILE) return 'profile';
    return 'home';
  };

  return (
    <div
      data-testid="dashboard-page"
      style={{
        height: '100vh',
        background: '#060606',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Ambient background glow (contained) */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Header controls */}
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          padding: '0 20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <Link
          to="/"
          data-testid="back-to-landing"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            color: '#8B93A7',
            textDecoration: 'none',
            fontSize: '13px',
            fontWeight: '500',
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          {t.backToSimo}
        </Link>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <LangToggle lang={lang} setLang={setLang} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              background: 'rgba(212,175,55,0.07)',
              border: '1px solid rgba(212,175,55,0.15)',
              borderRadius: '100px',
              padding: '5px 10px',
            }}
          >
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ADE80', boxShadow: '0 0 5px rgba(74,222,128,0.9)' }} />
            <span style={{ color: '#D4AF37', fontSize: '11px', fontWeight: '600' }}>{t.liveDemo}</span>
          </div>
        </div>
      </div>

      {/* Phone frame — responsive height */}
      <div
        style={{
          position: 'relative',
          width: '380px',
          maxWidth: 'calc(100vw - 0px)',
          height: 'min(720px, calc(100vh - 80px))',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            background: '#090909',
            borderRadius: '44px',
            border: '1.5px solid rgba(255,255,255,0.1)',
            boxShadow: '0 40px 100px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.07)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Dynamic Island */}
          <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', width: '110px', height: '28px', background: '#000', borderRadius: '20px', zIndex: 30 }} />

          {/* Status bar */}
          <div
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '50px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
              padding: '0 22px 8px', zIndex: 20,
              background: 'linear-gradient(to bottom, rgba(9,9,9,1) 50%, transparent)',
              pointerEvents: 'none',
            }}
          >
            <span style={{ color: '#F5F1EA', fontSize: '13px', fontWeight: '700' }}>9:41</span>
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <rect x="0" y="6" width="2.5" height="4" rx="0.5" fill="#F5F1EA"/>
                <rect x="3.5" y="4" width="2.5" height="6" rx="0.5" fill="#F5F1EA"/>
                <rect x="7" y="2" width="2.5" height="8" rx="0.5" fill="#F5F1EA"/>
                <rect x="10.5" y="0" width="2.5" height="10" rx="0.5" fill="#F5F1EA"/>
              </svg>
              <svg width="21" height="11" viewBox="0 0 21 11" fill="none">
                <rect x="0" y="1" width="17" height="9" rx="2" stroke="#F5F1EA" strokeWidth="1.2"/>
                <rect x="1" y="2" width="13" height="7" rx="1" fill="#F5F1EA"/>
                <path d="M18.5 4v3a1.5 1.5 0 000-3z" fill="#F5F1EA"/>
              </svg>
            </div>
          </div>

          {/* Screen content */}
          <div
            style={{
              position: 'absolute',
              top: '50px',
              left: 0,
              right: 0,
              bottom: '64px',
              overflow: 'hidden',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={screen}
                initial="initial"
                animate="in"
                exit="out"
                variants={phoneVariants}
                transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                style={{ height: '100%' }}
              >
                {renderScreen()}
              </motion.div>
            </AnimatePresence>

            {/* Toast */}
            <AnimatePresence>
              {toast && <Toast message={toast} />}
            </AnimatePresence>
          </div>

          {/* Bottom nav */}
          <BottomNav current={getNavTab(screen)} onNavigate={navigate} t={t} isRTL={isRTL} />

          {/* Home indicator */}
          <div style={{ position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '4px', background: 'rgba(255,255,255,0.18)', borderRadius: '2px', zIndex: 60 }} />
        </div>
      </div>

      {/* Caption */}
      <p style={{ color: '#8B93A7', fontSize: '11px', marginTop: '12px', position: 'relative', zIndex: 10, flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
        {t.interactiveDemo}
      </p>
    </div>
  );
};

const Dashboard = () => (
  <LanguageProvider>
    <WalletProvider>
      <DashboardInner />
    </WalletProvider>
  </LanguageProvider>
);

export default Dashboard;
